import { useCallback, useEffect, useRef, useState } from "react";

import { cn } from "@/lib/cn";
import { FOCUS } from "@/lib/interaction";

/**
 * 두 칸 사이를 잡아 끄는 손잡이.
 *
 * 보이는 것은 1px 선 하나지만 **닿는 곳은 그보다 넓다.** 선 두께대로 만들면
 * 조준하다 옆 목록을 눌러 대화가 열린다. 손이 닿는 범위와 눈에 보이는 굵기는
 * 다른 문제다.
 *
 * 마우스만 있는 장치가 아니다. 끌기 말고도 **키보드(방향키)와 더블클릭(되돌리기)**
 * 으로 같은 일을 할 수 있어야 한다 — 끌기는 정밀한 조작이라 손이 떨리거나
 * 트랙패드만 쓰는 사람에게는 어렵다.
 */

type ResizeHandleProps = {
  /** 스크린리더가 읽을 이름. "방 목록 폭"처럼 무엇을 조절하는지 적는다 */
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  /** 더블클릭·Home으로 기본값에 되돌린다 */
  onReset: () => void;
  /** 배치만 — 칸이 하나뿐인 좁은 화면에서는 숨긴다 */
  className?: string;
  /**
   * 평소에도 선을 그린다. 양쪽 배경색이 같아 경계가 안 보이는 자리에 쓴다.
   * 레일처럼 색이 확 다른 경계에서는 선을 하나 더 그으면 지저분하다.
   */
  divider?: boolean;
};

/** 방향키 한 번에 움직이는 거리. Shift를 누르면 성큼 간다. */
const STEP = 16;
const STEP_LARGE = 48;

export function ResizeHandle({
  label,
  value,
  min,
  max,
  onChange,
  onReset,
  divider = false,
  className,
}: ResizeHandleProps) {
  const [dragging, setDragging] = useState(false);
  /** 끌기 시작한 지점과 그때의 폭. 매 프레임 누적하면 오차가 쌓인다 */
  const origin = useRef({ x: 0, width: 0 });

  /**
   * 끄는 동안 몸통 전체의 커서를 바꾸고 글자 선택을 끈다.
   *
   * 손잡이에만 걸면 포인터가 조금만 벗어나도 커서가 되돌아가고, 지나간 자리의
   * 방 이름들이 파랗게 선택된다 — 폭을 조절한 게 아니라 뭔가 잘못 끈 것처럼 보인다.
   */
  useEffect(() => {
    if (!dragging) return;

    document.body.classList.add("is-resizing");
    return () => document.body.classList.remove("is-resizing");
  }, [dragging]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const step = event.shiftKey ? STEP_LARGE : STEP;

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        onChange(value - step);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        onChange(value + step);
      } else if (event.key === "Home") {
        event.preventDefault();
        onReset();
      }
    },
    [value, onChange, onReset],
  );

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={Math.round(value)}
      aria-valuemin={Math.round(min)}
      aria-valuemax={Math.round(max)}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onDoubleClick={onReset}
      onPointerDown={(event) => {
        // 오른쪽 버튼이나 두 번째 손가락으로는 끌지 않는다.
        if (event.button !== 0) return;
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        origin.current = { x: event.clientX, width: value };
        setDragging(true);
      }}
      onPointerMove={(event) => {
        if (!dragging) return;
        onChange(origin.current.width + (event.clientX - origin.current.x));
      }}
      onPointerUp={(event) => {
        if (!dragging) return;
        event.currentTarget.releasePointerCapture(event.pointerId);
        setDragging(false);
      }}
      onPointerCancel={() => setDragging(false)}
      className={cn(
        // touch-none이 없으면 터치 화면에서 끌기가 스크롤로 먹힌다.
        "group relative z-10 flex w-1.5 shrink-0 cursor-col-resize touch-none justify-center",
        FOCUS,
        className,
      )}
    >
      <span
        className={cn(
          "w-px transition-colors duration-120 ease-out-soft",
          divider ? "bg-line" : "bg-transparent",
          // 끄는 동안에는 손을 뗄 때까지 굵게 남는다. 어디를 잡고 있는지 보여야 한다.
          dragging ? "bg-line-strong w-0.5" : "group-hover:bg-line-strong",
        )}
      />
    </div>
  );
}
